import { checkUserLimit } from "../../utils/planLimits";
import { useEffect, useState } from "react";
import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import SocieteAndUserService from "../../services/societeAndUser";
import getCurrentUserId from "../../reduxSaga/sagas/requests/currentUserId";

const ROLE_NAME = "WAREHOUSE_MANAGER";

/*
 * Quick "new warehouse manager" dialog (used while creating a warehouse). It
 * creates a user with the WAREHOUSE_MANAGER role through the same endpoint as
 * the Personnels page and returns the created user to the caller.
 */
const QuickAddWarehouseManager = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({
    lastName: "",
    firstName: "",
    cin: "",
    email: "",
    phone: "",
    pwd: "",
    rePWD: "",
  });
  const [roleId, setRoleId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    SocieteAndUserService.getAllRoles()
      .then((response) => {
        const roles = response.data && response.data.Role ? response.data.Role : [];
        const role = roles.find((r) => r.name === ROLE_NAME);
        if (role) {
          setRoleId(role.id);
        } else {
          setError("Le rôle responsable de dépôt est introuvable");
        }
      })
      .catch(() => setError("Erreur : il y a un problème de connexion"));
  }, []);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = () => {
    if (saving) return;
    const missing = [
      ["lastName", "le nom"],
      ["firstName", "le prénom"],
      ["cin", "le CIN"],
      ["email", "l'email"],
      ["phone", "le téléphone"],
      ["pwd", "le mot de passe"],
    ].find(([field]) => form[field].trim() === "");
    if (missing) {
      setError("Renseignez " + missing[1]);
      return;
    }
    if (form.pwd !== form.rePWD) {
      setError("Les deux mots de passe sont différents");
      return;
    }
    if (roleId === null) {
      setError("Le rôle responsable de dépôt est introuvable");
      return;
    }
    setSaving(true);
    setError("");
    checkUserLimit(1).then((limit) => {
      if (!limit.allowed) {
        setError(limit.message);
        setSaving(false);
        return;
      }
      createUser();
    });
  };

  const createUser = () => {
    const user = {
      cin: form.cin.trim(),
      email: form.email.trim(),
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone.trim(),
      idRole: [String(roleId)],
      idSociete: 0,
      idUserCreation: getCurrentUserId(),
      pwd: form.pwd,
      rePWD: form.rePWD,
    };
    SocieteAndUserService.addAdminClient(user)
      .then((response) => {
        const data = response.data || {};
        if (data.success && data.success.id !== undefined) {
          onCreated({
            id: data.success.id,
            firstName: data.success.firstName || user.firstName,
            lastName: data.success.lastName || user.lastName,
          });
          onClose();
        } else if (data.errorExist) {
          setError("Cet email existe déjà");
          setSaving(false);
        } else if (data.errorPassword) {
          setError("Mot de passe invalide");
          setSaving(false);
        } else {
          setError("Le responsable n'a pas pu être créé");
          setSaving(false);
        }
      })
      .catch(() => {
        setError("Erreur : il y a un problème de connexion");
        setSaving(false);
      });
  };

  return (
    <Dialog open={true} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Nouveau responsable de dépôt</DialogTitle>
      <DialogContent>
        <div className="row mt-2">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmLast">Nom <span style={{ color: "red" }}>*</span></label>
              <input id="qmLast" className="form-control" value={form.lastName} onChange={set("lastName")} autoFocus />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmFirst">Prénom <span style={{ color: "red" }}>*</span></label>
              <input id="qmFirst" className="form-control" value={form.firstName} onChange={set("firstName")} />
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmCin">CIN <span style={{ color: "red" }}>*</span></label>
              <input id="qmCin" className="form-control" value={form.cin} onChange={set("cin")} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmPhone">Téléphone <span style={{ color: "red" }}>*</span></label>
              <input id="qmPhone" className="form-control" value={form.phone} onChange={set("phone")} />
            </div>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="qmEmail">Email <span style={{ color: "red" }}>*</span></label>
          <input id="qmEmail" type="email" className="form-control" value={form.email} onChange={set("email")} />
        </div>
        <div className="row">
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmPwd">Mot de passe <span style={{ color: "red" }}>*</span></label>
              <input id="qmPwd" type="password" autoComplete="new-password" className="form-control" value={form.pwd} onChange={set("pwd")} />
            </div>
          </div>
          <div className="col-sm-6">
            <div className="form-group">
              <label htmlFor="qmRePwd">Confirmer <span style={{ color: "red" }}>*</span></label>
              <input id="qmRePwd" type="password" autoComplete="new-password" className="form-control" value={form.rePWD} onChange={set("rePWD")} />
            </div>
          </div>
        </div>
        {error && <div className="alert alert-danger mb-0">{error}</div>}
      </DialogContent>
      <DialogActions>
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={saving}>
          Annuler
        </button>
        <button type="button" className="btn btn-dark" onClick={submit} disabled={saving}>
          <i className="fas fa-user-plus mr-1"></i>Ajouter le responsable
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickAddWarehouseManager;
